import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Phone, Mail, MapPin, Clock, MessageCircle, CheckCircle2, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MaskLine, Reveal, FadeIn, Overline } from "@/components/motion";
import { CONTACTS, WA_URL } from "@/lib/site";
import { toast } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { num: "1", title: "Invii la richiesta", text: "Nome, contatti, esame di interesse e una breve descrizione della tua situazione." },
  { num: "2", title: "Ti rispondiamo entro 24 ore", text: "Confermiamo la disponibilità oppure ti proponiamo un'alternativa." },
  { num: "3", title: "Confermi senza impegno", text: "La richiesta non è una prenotazione automatica: decidi tu se accettare la proposta." },
];

const ESAMI = [
  "Elettromiografia (EMG)",
  "Elettroencefalogramma (EEG)",
  "Poligrafia del sonno",
  "Polisonnografia",
];

const URGENZA = [
  "Subito – entro 2 settimane",
  "Entro 1 mese",
  "Entro 2-3 mesi",
];

function ContactCol() {
  return (
    <div className="space-y-10">
      <Reveal>
        <div className="space-y-5">
          <a href={CONTACTS.telefonoHref} className="flex items-center gap-4 group" data-testid="contact-phone">
            <span className="flex items-center justify-center w-11 h-11 rounded-full border border-indaco/25 text-indaco transition-colors duration-300 group-hover:bg-indaco group-hover:text-crema">
              <Phone className="w-4 h-4" />
            </span>
            <div>
              <p className="text-sm uppercase tracking-[0.18em] text-viole">Telefono</p>
              <p className="font-sora font-semibold text-ink">{CONTACTS.telefono}</p>
            </div>
          </a>
          <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group" data-testid="contact-whatsapp">
            <span className="flex items-center justify-center w-11 h-11 rounded-full border border-indaco/25 text-indaco transition-colors duration-300 group-hover:bg-indaco group-hover:text-crema">
              <MessageCircle className="w-4 h-4" />
            </span>
            <div>
              <p className="text-sm uppercase tracking-[0.18em] text-viole">WhatsApp</p>
              <p className="font-sora font-semibold text-ink">{CONTACTS.whatsappDisplay}</p>
            </div>
          </a>
          <a href={`mailto:${CONTACTS.email}`} className="flex items-center gap-4 group" data-testid="contact-email">
            <span className="flex items-center justify-center w-11 h-11 rounded-full border border-indaco/25 text-indaco transition-colors duration-300 group-hover:bg-indaco group-hover:text-crema">
              <Mail className="w-4 h-4" />
            </span>
            <div>
              <p className="text-sm uppercase tracking-[0.18em] text-viole">Email</p>
              <p className="font-sora font-semibold text-ink break-all">{CONTACTS.email}</p>
            </div>
          </a>
          <div className="flex items-center gap-4">
            <span className="flex items-center justify-center w-11 h-11 rounded-full border border-indaco/25 text-indaco">
              <MapPin className="w-4 h-4" />
            </span>
            <div>
              <p className="text-sm uppercase tracking-[0.18em] text-viole">Dove siamo</p>
              <p className="font-sora font-semibold text-ink">{CONTACTS.indirizzo}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="flex items-center justify-center w-11 h-11 rounded-full border border-indaco/25 text-indaco">
              <Clock className="w-4 h-4" />
            </span>
            <div>
              <p className="text-sm uppercase tracking-[0.18em] text-viole">Orari</p>
              <p className="font-sora font-semibold text-ink">{CONTACTS.orari}</p>
            </div>
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.15}>
        <div className="rounded-lg bg-crema-warm border border-ink/5 p-8">
          <p className="text-sm uppercase tracking-[0.22em] text-viole mb-6">Come funziona</p>
          <div className="space-y-6">
            {STEPS.map((s) => (
              <div key={s.num} className="flex gap-5">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-indaco text-crema font-sora font-bold text-[15px] md:text-base shrink-0">
                  {s.num}
                </span>
                <div>
                  <p className="font-sora font-semibold text-ink text-[15px] md:text-base">{s.title}</p>
                  <p className="text-viole text-[15px] md:text-base mt-1 leading-relaxed">{s.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Reveal>
    </div>
  );
}

function RequestForm() {
  const [form, setForm] = useState({
    nome: "",
    cognome: "",
    email: "",
    telefono: "",
    esame: "",
    situazione: "",
    urgenza: "",
    consenso: false,
    consenso_whatsapp: false,
  });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const isValid =
    form.nome.trim() &&
    form.cognome.trim() &&
    emailOk &&
    form.telefono.trim() &&
    form.esame &&
    form.situazione.trim() &&
    form.urgenza &&
    form.consenso &&
    form.consenso_whatsapp;

  const validate = () => {
    const e = {};
    if (!form.nome.trim()) e.nome = "Inserisci il tuo nome";
    if (!form.cognome.trim()) e.cognome = "Inserisci il tuo cognome";
    if (!emailOk) e.email = "Inserisci un indirizzo email valido";
    if (!form.telefono.trim()) e.telefono = "Inserisci un numero di telefono";
    if (!form.esame) e.esame = "Seleziona l'esame di interesse";
    if (!form.situazione.trim()) e.situazione = "Descrivi brevemente la tua situazione";
    if (!form.urgenza) e.urgenza = "Seleziona un'opzione";
    if (!form.consenso) e.consenso = "Il consenso è necessario per ricontattarti";
    if (!form.consenso_whatsapp) e.consenso_whatsapp = "Il consenso è necessario per ricontattarti";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await axios.post(`${API}/appointment-requests`, form);
      setSent(true);
      toast.success("Richiesta inviata. Ti risponderemo entro 24 ore.");
    } catch (err) {
      toast.error("Qualcosa non ha funzionato. Riprova o scrivici su WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <FadeIn>
        <div className="rounded-lg bg-crema border border-indaco/20 p-10 text-center" data-testid="form-success">
          <CheckCircle2 className="w-12 h-12 text-indaco mx-auto" />
          <h3 className="mt-6 font-sora font-bold tracking-tight text-2xl text-ink">Richiesta ricevuta.</h3>
          <p className="mt-4 text-[17px] md:text-lg text-viole leading-relaxed max-w-md mx-auto">
            Ti scriviamo entro 24 ore con una proposta di orario, dal lunedì al venerdì.
            Se hai fretta, puoi scriverci anche su WhatsApp.
          </p>
          <a
            href={WA_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-3 rounded-full bg-indaco px-8 py-4 text-[15px] md:text-base font-semibold text-crema transition-colors duration-300 hover:bg-indaco-deep"
            data-testid="success-whatsapp"
          >
            <MessageCircle className="w-4 h-4" />
            Scrivici su WhatsApp
          </a>
        </div>
      </FadeIn>
    );
  }

  const fieldCls = "bg-crema border-ink/15 focus-visible:ring-indaco h-12 rounded-md";
  const labelCls = "text-sm uppercase tracking-[0.18em] text-viole block mb-2";
  const err = (k) => errors[k] && <p className="text-arancio text-sm mt-1.5" data-testid={`error-${k}`}>{errors[k]}</p>;

  return (
    <Reveal delay={0.1}>
      <form onSubmit={submit} className="rounded-lg bg-crema-warm border border-ink/5 p-8 lg:p-10 space-y-6" data-testid="appointment-form" noValidate>
        <div>
          <h2 className="font-sora font-bold tracking-tight text-2xl text-ink">Richiedi un appuntamento</h2>
          <p className="text-[15px] md:text-base text-viole mt-2">Non è una prenotazione automatica: ti proponiamo un orario entro 24 ore, senza impegno.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label htmlFor="nome" className={labelCls}>Nome *</label>
            <Input id="nome" value={form.nome} onChange={(e) => set("nome", e.target.value)} className={fieldCls} placeholder="Mario" data-testid="form-nome" />
            {err("nome")}
          </div>
          <div>
            <label htmlFor="cognome" className={labelCls}>Cognome *</label>
            <Input id="cognome" value={form.cognome} onChange={(e) => set("cognome", e.target.value)} className={fieldCls} placeholder="Rossi" data-testid="form-cognome" />
            {err("cognome")}
          </div>
        </div>

        <div>
          <label htmlFor="email" className={labelCls}>Email *</label>
          <Input id="email" type="email" inputMode="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={fieldCls} placeholder="mario.rossi@email.it" data-testid="form-email" />
          {err("email")}
        </div>

        <div>
          <label htmlFor="telefono" className={labelCls}>Telefono o WhatsApp *</label>
          <Input id="telefono" type="tel" inputMode="tel" value={form.telefono} onChange={(e) => set("telefono", e.target.value)} className={fieldCls} placeholder="+39 ..." data-testid="form-telefono" />
          {err("telefono")}
        </div>

        <div>
          <label className={labelCls}>Esame di interesse *</label>
          <Select value={form.esame} onValueChange={(v) => set("esame", v)}>
            <SelectTrigger className={fieldCls} data-testid="form-esame">
              <SelectValue placeholder="Seleziona una prestazione" />
            </SelectTrigger>
            <SelectContent className="bg-crema">
              {ESAMI.map((o) => (
                <SelectItem key={o} value={o} data-testid={`form-esame-${o.slice(0, 12).replace(/\W+/g, "-").toLowerCase()}`}>
                  {o}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {err("esame")}
        </div>

        <div>
          <label htmlFor="situazione" className={labelCls}>Descrivi brevemente la tua situazione attuale *</label>
          <Textarea
            id="situazione"
            value={form.situazione}
            onChange={(e) => set("situazione", e.target.value)}
            className="bg-crema border-ink/15 focus-visible:ring-indaco rounded-md min-h-[120px]"
            placeholder="Da quanto dura, cosa senti, cosa hai già fatto. Bastano poche righe."
            data-testid="form-situazione"
          />
          {err("situazione")}
        </div>

        <div>
          <label className={labelCls}>Con quanta urgenza vorresti risolverla? *</label>
          <Select value={form.urgenza} onValueChange={(v) => set("urgenza", v)}>
            <SelectTrigger className={fieldCls} data-testid="form-urgenza">
              <SelectValue placeholder="Seleziona" />
            </SelectTrigger>
            <SelectContent className="bg-crema">
              {URGENZA.map((o, i) => (
                <SelectItem key={o} value={o} data-testid={`form-urgenza-${i}`}>
                  {o}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {err("urgenza")}
        </div>

        <div>
          <div className="flex items-start gap-3">
            <Checkbox id="consenso" checked={form.consenso} onCheckedChange={(v) => set("consenso", v === true)} className="mt-1" data-testid="form-consenso" />
            <label htmlFor="consenso" className="text-[15px] md:text-base text-ink/80 leading-relaxed cursor-pointer">
              Acconsento al trattamento dei miei dati personali, inclusi quelli relativi alla salute,
              per essere ricontattato in merito a questa richiesta, come descritto nell'
              <Link to="/privacy" className="text-indaco underline underline-offset-4" data-testid="form-privacy-link">informativa privacy</Link>. *
            </label>
          </div>
          {err("consenso")}
        </div>

        <div>
          <div className="flex items-start gap-3">
            <Checkbox id="consenso_whatsapp" checked={form.consenso_whatsapp} onCheckedChange={(v) => set("consenso_whatsapp", v === true)} className="mt-1" data-testid="form-consenso-whatsapp" />
            <label htmlFor="consenso_whatsapp" className="text-[15px] md:text-base text-ink/80 leading-relaxed cursor-pointer">
              Acconsento a essere ricontattato al numero indicato, anche tramite WhatsApp. *
            </label>
          </div>
          {err("consenso_whatsapp")}
        </div>

        <button
          type="submit"
          disabled={!isValid || sending}
          className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-indaco px-8 py-4 text-[15px] md:text-base font-semibold text-crema transition-colors duration-300 hover:bg-indaco-deep disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="form-submit"
        >
          {sending && <Loader2 className="w-4 h-4 animate-spin" />}
          {sending ? "Invio in corso..." : "Invia la richiesta"}
        </button>
        <p className="text-sm text-viole text-center" data-testid="form-microcopy">
          Nessun impegno · Non serve l'impegnativa · Ti rispondiamo entro 24 ore
        </p>
      </form>
    </Reveal>
  );
}

export default function Prenota() {
  return (
    <>
      <section className="bg-indaco-deep text-crema pt-40 pb-20 lg:pt-52 lg:pb-28 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute -right-40 -top-10 w-[420px] h-[420px] rounded-full border border-crema/10" />
          <div className="absolute -right-16 top-24 w-[420px] h-[420px] rounded-full border border-arancio/20" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <FadeIn>
            <Overline light>Prenota e contatti</Overline>
          </FadeIn>
          <h1 className="mt-8 font-sora font-extrabold tracking-tighter text-4xl md:text-5xl lg:text-6xl leading-[1.05]" data-testid="prenota-title">
            <MaskLine delay={0.15}>Raccontaci la tua</MaskLine>
            <MaskLine delay={0.3}>
              <span className="text-arancio">situazione attuale</span>
            </MaskLine>
          </h1>
          <FadeIn delay={0.6}>
            <p className="mt-8 max-w-2xl text-crema/70 text-lg leading-relaxed">
              Su WhatsApp o con il modulo qui sotto. Non è una prenotazione automatica.
              Ti rispondiamo entro 24 ore, dal lunedì al venerdì.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="bg-crema py-24 lg:py-36" data-testid="prenota-main">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16">
          <ContactCol />
          <RequestForm />
        </div>
      </section>
    </>
  );
}
